// Formatting helpers used across the admin UI.

export function humaniseDate(input: string | Date | null | undefined): string {
  if (!input) return '—';
  const date = typeof input === 'string' ? new Date(input) : input;
  if (Number.isNaN(date.getTime())) return '—';

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.round(diffMs / 1000);
  const diffMin = Math.round(diffSec / 60);
  const diffHr = Math.round(diffMin / 60);

  // Relative format only inside the 24-hour window — that's when
  // "Xm ago / Xh ago" is genuinely useful for skimming "is this
  // fresh?". Past 24h the relative form ("3d ago", "11d ago") stops
  // helping and starts costing precision: was that Monday morning or
  // Tuesday evening? Switch to the full local datetime so the agent
  // sees the actual day + time at a glance, no hover needed.
  if (Math.abs(diffSec) < 45) return 'just now';
  if (Math.abs(diffMin) < 60) return `${diffMin}m ago`;
  if (Math.abs(diffHr) < 24) return `${diffHr}h ago`;

  // >24h → defer to the full datetime formatter (viewer's TZ).
  return formatDateTime(date);
}

/** Ultra-compact timestamp for dense list rows — never wider than ~6
 *  characters, so it can't wrap and every row stays the same height.
 *
 *  `humaniseDate` switches to the full "13 Aug 2026, 03:35 pm" past 24 hours,
 *  which is right for a detail view but breaks a list: at the ~110px a list
 *  column can spare it wrapped onto two lines, and combined with an SLA line
 *  overflowed the fixed 52px row and clipped the status badge beside it.
 *
 *  The precise datetime isn't lost — `RelativeTime` still puts it in the
 *  tooltip and in the machine-readable `dateTime` attribute. */
export function compactDate(
  input: string | Date | null | undefined,
  now: Date = new Date(),
): string {
  if (!input) return '—';
  const date = typeof input === 'string' ? new Date(input) : input;
  if (Number.isNaN(date.getTime())) return '—';

  const diffMs = now.getTime() - date.getTime();
  const mins = Math.round(Math.abs(diffMs) / 60_000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.round(hrs / 24);
  if (days < 7) return `${days}d`;
  // Past a week, a weekday-relative number stops helping — show the date.
  // Year is omitted within the current year to save the width.
  const sameYear = date.getFullYear() === now.getFullYear();
  return date.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: '2-digit' }),
  });
}

export function formatDateTime(input: string | Date | null | undefined): string {
  if (!input) return '—';
  const date = typeof input === 'string' ? new Date(input) : input;
  if (Number.isNaN(date.getTime())) return '—';
  // Renders in the VIEWER's timezone. CLAUDE.md §7 mandates
  // Australia/Sydney for cron + SLA *math* (server side) — that's about
  // computing due times consistently, not about how we show timestamps
  // to a human looking at the screen. Showing every team member their
  // own local time matches how every other modern app behaves (Slack,
  // Linear, Gmail) and removes the constant "what time was that in MY
  // morning?" mental conversion. The 'en-AU' locale stays — it's the
  // FORMAT style (d Mon, 24h) not the TZ.
  return date.toLocaleString('en-AU', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export type Priority = 'P1' | 'P2' | 'P3' | 'P4';
export type TicketStatus =
  | 'new'
  | 'in_progress'
  | 'waiting_on_client'
  | 'resolved'
  | 'closed'
  | 'escalated';

// ── Semantic tints ──────────────────────────────────────────────────────────
// Linear-style subtle pills: tinted surface + saturated text + 1px inset ring.
// Borders are intentionally absent — the consumer renders a `border-transparent`
// Badge and relies on the ring for separation.
//
// These used to return hard-coded Tailwind palette strings — literally
// "bg-red-50 text-red-700 ring-red-200" and so on. Two problems with that:
// they couldn't follow a theme, so dark mode was impossible; and because the
// strings were literals, the same concept drifted between files —
// `waiting_on_client` was purple here and amber in ContactTicketsPanel and
// ContactTicketsTimeline, so one ticket showed two different colours on two
// surfaces.
//
// Now every mapping goes through a named role backed by CSS custom properties
// (see the `--tint-*` block in index.css). Re-hueing a concept is one line
// there, it themes automatically, and there is exactly one table per concept.
export type TintRole =
  | 'critical'
  | 'high'
  | 'warning'
  | 'info'
  | 'active'
  | 'pending'
  | 'success'
  | 'neutral';

/** Chip recipe for a tint role — surface, text, ring. */
export function tintClasses(role: TintRole): string {
  switch (role) {
    case 'critical':
      return 'bg-tint-critical text-tint-critical-fg ring-1 ring-inset ring-tint-critical-ring';
    case 'high':
      return 'bg-tint-high text-tint-high-fg ring-1 ring-inset ring-tint-high-ring';
    case 'warning':
      return 'bg-tint-warning text-tint-warning-fg ring-1 ring-inset ring-tint-warning-ring';
    case 'info':
      return 'bg-tint-info text-tint-info-fg ring-1 ring-inset ring-tint-info-ring';
    case 'active':
      return 'bg-tint-active text-tint-active-fg ring-1 ring-inset ring-tint-active-ring';
    case 'pending':
      return 'bg-tint-pending text-tint-pending-fg ring-1 ring-inset ring-tint-pending-ring';
    case 'success':
      return 'bg-tint-success text-tint-success-fg ring-1 ring-inset ring-tint-success-ring';
    case 'neutral':
    default:
      return 'bg-tint-neutral text-tint-neutral-fg ring-1 ring-inset ring-tint-neutral-ring';
  }
}

/** P1 is the only priority that reads as an emergency; P4 is deliberately mute. */
export function priorityRole(priority: string | null | undefined): TintRole {
  switch (priority) {
    case 'P1':
      return 'critical';
    case 'P2':
      return 'high';
    case 'P3':
      return 'warning';
    default:
      return 'neutral';
  }
}

export function statusRole(status: string | null | undefined): TintRole {
  switch (status) {
    case 'new':
      return 'info';
    case 'in_progress':
      return 'active';
    case 'waiting_on_client':
      return 'pending';
    case 'resolved':
      return 'success';
    case 'escalated':
      return 'critical';
    default:
      return 'neutral';
  }
}

export function sentimentRole(sentiment: string | null | undefined): TintRole {
  switch (sentiment) {
    case 'positive':
      return 'success';
    case 'negative':
      return 'critical';
    default:
      return 'neutral';
  }
}

export function priorityColor(priority: string | null | undefined): string {
  return tintClasses(priorityRole(priority));
}

/** Solid dot for compact rows that have no room for a full pill. Uses the
 *  role's *foreground* token so the dot matches the text colour of the pill
 *  the same ticket wears elsewhere — the contact panel and timeline each used
 *  to hand-roll this, and had drifted (P2 amber vs orange, P3 yellow vs amber). */
export function priorityDot(priority: string | null | undefined): string {
  switch (priorityRole(priority)) {
    case 'critical':
      return 'bg-tint-critical-fg';
    case 'high':
      return 'bg-tint-high-fg';
    case 'warning':
      return 'bg-tint-warning-fg';
    default:
      return 'bg-tint-neutral-fg';
  }
}

export function statusColor(status: string | null | undefined): string {
  return tintClasses(statusRole(status));
}

/** Solid dot in the status hue, for the dense ticket list.
 *
 *  A filled status pill on EVERY row put a second full-colour system beside
 *  the priority chips, so the list read as a wall of colour and nothing stood
 *  out. The dashboard's own filter bar already made this call in the other
 *  direction — its comment says status chips stay neutral because "using
 *  semantic color here would compete with priority and double-color the row".
 *  The row now follows the same rule: a small hue dot keeps status scannable
 *  at a glance, with a fraction of the ink, and lets the priority chip be the
 *  only saturated block on the row. */
export function statusDot(status: string | null | undefined): string {
  switch (statusRole(status)) {
    case 'info':
      return 'bg-tint-info-fg';
    case 'active':
      return 'bg-tint-active-fg';
    case 'pending':
      return 'bg-tint-pending-fg';
    case 'success':
      return 'bg-tint-success-fg';
    case 'critical':
      return 'bg-tint-critical-fg';
    default:
      return 'bg-tint-neutral-fg';
  }
}

export function sentimentColor(sentiment: string | null | undefined): string {
  return tintClasses(sentimentRole(sentiment));
}

/** Human label for a ticket status — replaces the ad-hoc `.replace(/_/g,' ')`
 *  calls that produced lowercase "waiting on client" next to Title-Case tabs,
 *  and the two private STATUS_LABEL tables that had drifted alongside the
 *  colour tables. */
export function statusLabel(status: string | null | undefined): string {
  switch (status) {
    case 'new':
      return 'New';
    case 'in_progress':
      return 'In progress';
    case 'waiting_on_client':
      return 'Waiting on client';
    case 'resolved':
      return 'Resolved';
    case 'closed':
      return 'Closed';
    case 'escalated':
      return 'Escalated';
    default:
      return status ? status.replace(/_/g, ' ') : '—';
  }
}

// First-letter-of-first-word + first-letter-of-last-word from a name. e.g.
// "Rex Example" → "RE", "Sarah" → "S", "Sarah Marie Smith" → "SS".
export function nameInitials(name: string | null | undefined): string {
  if (!name) return '';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return (parts[0].slice(0, 1) + parts[parts.length - 1].slice(0, 1)).toUpperCase();
}

// Resolve a slack_user_id to display initials. If a team_member match exists,
// use the human's name; otherwise fall back to a short form of the slack ID.
export function assigneeInitials(
  slackId: string | null | undefined,
  teamMembersBySlackId?: Map<string, { name?: string }>,
): string {
  if (!slackId) return '';
  const member = teamMembersBySlackId?.get(slackId);
  if (member?.name) return nameInitials(member.name);
  // Fall back to first 2 chars of the slack ID (without the U/W/B prefix).
  return slackId.replace(/^[UWB]/, '').slice(0, 2).toUpperCase();
}

// Resolve a slack_user_id to a human display name (or the slack ID if unknown).
export function assigneeName(
  slackId: string | null | undefined,
  teamMembersBySlackId?: Map<string, { name?: string }>,
): string {
  if (!slackId) return '';
  const member = teamMembersBySlackId?.get(slackId);
  return member?.name || slackId;
}

// `slackIdInitials` used to live here, marked "Deprecated — kept for
// back-compat with any stale callers. Prefer `assigneeInitials`." It had zero
// callers, so the back-compat it was kept for did not exist. Removed.

// Soft tinted colour classes used for the icon chip on each notification row.
// Same role vocabulary as priorityColor / statusColor.
export function notificationAccent(type: string | null | undefined): string {
  switch (type) {
    case 'sla_breach':
    case 'ticket_escalated':
      return tintClasses('critical');
    case 'client_replied':
    case 'reassigned_to_you':
    case 'new_ticket_assigned':
      return tintClasses('info');
    case 'follow_up_sent':
    case 'follow_up_reminder':
      return tintClasses('active');
    case 'ticket_resolved':
      return tintClasses('success');
    case 'note_added':
    case 'note_mention':
      return tintClasses('warning');
    default:
      return tintClasses('neutral');
  }
}

export function emailDomain(email: string | null | undefined): string | null {
  if (!email) return null;
  const at = email.lastIndexOf('@');
  if (at === -1) return null;
  return email.slice(at + 1).toLowerCase();
}

// Compact SLA countdown for ticket rows.
//   "due 2h" / "due 30m" — ample time
//   "due 25m" amber       — under 30 min
//   "overdue 12m" rose    — past the wire
//
// Returns null when there's no due time (e.g. backfilled tickets).
export interface SlaCountdown {
  label: string;
  /** Chip-sized form for dense rows — "4d over" / "25m left". Carries the same
   *  information as `label` in about half the width, so the ticket list can
   *  show the actual number instead of a bare "SLA" flag. */
  shortLabel: string;
  /** 'ok' = >30m left · 'soon' = ≤30m left · 'breached' = past due */
  state: 'ok' | 'soon' | 'breached';
  /** Tailwind color class for the time text */
  className: string;
}

export function slaCountdown(
  dueAt: string | null | undefined,
  now: Date = new Date(),
): SlaCountdown | null {
  if (!dueAt) return null;
  const due = new Date(dueAt);
  if (Number.isNaN(due.getTime())) return null;

  const diffMs = due.getTime() - now.getTime();
  const overdue = diffMs < 0;
  const absMin = Math.abs(diffMs) / 60_000;

  let valueLabel: string;
  if (absMin < 60) valueLabel = `${Math.max(1, Math.round(absMin))}m`;
  else if (absMin < 24 * 60) valueLabel = `${(Math.round(absMin / 6) / 10).toFixed(1)}h`;
  else valueLabel = `${Math.round(absMin / 1440)}d`;

  // The label carries the state in words, not just in hue. Previously "due 25m"
  // (amber, urgent) and "due 2.5h" (grey, fine) differed by colour alone, which
  // is both a WCAG 1.4.1 problem and a practical one — roughly 8% of men can't
  // separate that amber from that grey at 10px.
  if (overdue) {
    return {
      label: `overdue ${valueLabel}`,
      shortLabel: `${valueLabel} over`,
      state: 'breached',
      className: 'text-tint-critical-fg font-semibold',
    };
  }
  if (absMin <= 30) {
    return {
      label: `due in ${valueLabel}`,
      shortLabel: `${valueLabel} left`,
      state: 'soon',
      className: 'text-tint-warning-fg font-semibold',
    };
  }
  return {
    label: `due in ${valueLabel}`,
    shortLabel: `${valueLabel} left`,
    state: 'ok',
    className: 'text-muted-foreground',
  };
}
